import { LitElement, html } from "@lit"

class MobileMenuPanel extends LitElement {
  constructor() {
    super()

    this.$container = this.querySelector(".mobile-menu__container")
    this.parentItems = this.querySelectorAll(".js-mobile-parent-item")
    this.subPanels = this.querySelectorAll(".mobile-menu__sub-panel")
    this.closeSubpanelBtn = this.querySelector(".js-submenu-close-button")
    this.menuCloseBtn = this.querySelector(".js-menu-close-button")

    this.allFocusableElements = []

    this._handleSubMenuOpen = this._handleSubMenuOpen.bind(this)
    this._handleSubMenuClose = this._handleSubMenuClose.bind(this)

    this._activeSubPanel = null
    this._activeToggle = null
    this._boundKeydownHandler = this._handleSubMenuKeydown.bind(this)
    this._handleDetailsToggle = this._handleDetailsToggle.bind(this)
    this._handleSubmenuSummaryClick = this._handleSubmenuSummaryClick.bind(this)
    this._handleParentItemKeydown = this._handleParentItemKeydown.bind(this)
    this._handleCloseSubpanelClick = this._handleCloseSubpanelClick.bind(this)

    this._allPotentialFocusableElements = []
    this.submenuDetails = []
    this._detailsAnimations = new WeakMap()
    this._motionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
  }

  connectedCallback() {
    super.connectedCallback()

    for (const item of this.parentItems) {
      item.addEventListener("click", this._handleSubMenuOpen)
      item.addEventListener("keydown", this._handleParentItemKeydown)
    }

    if (this.closeSubpanelBtn) {
      this.closeSubpanelBtn.addEventListener("click", this._handleCloseSubpanelClick)
      this.closeSubpanelBtn.setAttribute("tabindex", "-1")
    }

    this.addEventListener("keydown", this._boundKeydownHandler)

    this.submenuDetails = Array.from(
      this.querySelectorAll("details.mobile-menu__submenu"),
    )
    for (const details of this.submenuDetails) {
      const summary = details.querySelector("summary.mobile-menu__submenu__summary")
      if (summary) {
        summary.addEventListener("click", this._handleSubmenuSummaryClick)
      }
      this._updateSubmenuItemsTabindex(details)
      this._syncDetailsAria(details)
      details.addEventListener("toggle", this._handleDetailsToggle)
    }

    this._collectAllPotentialFocusableElements()
    this._refreshFocusableElements()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    for (const item of this.parentItems) {
      item.removeEventListener("click", this._handleSubMenuOpen)
      item.removeEventListener("keydown", this._handleParentItemKeydown)
    }
    if (this.closeSubpanelBtn) {
      this.closeSubpanelBtn.removeEventListener("click", this._handleCloseSubpanelClick)
    }
    this.removeEventListener("keydown", this._boundKeydownHandler)

    for (const details of this.submenuDetails) {
      const summary = details.querySelector("summary.mobile-menu__submenu__summary")
      if (summary) {
        summary.removeEventListener("click", this._handleSubmenuSummaryClick)
      }
      this._cancelDetailsAnimation(details)
      details.removeEventListener("toggle", this._handleDetailsToggle)
    }
  }

  _handleDetailsToggle(event) {
    const details = event.target.closest("details.mobile-menu__submenu")
    if (!details) return
    this._updateSubmenuItemsTabindex(details)
    this._syncDetailsAria(details)
    this._refreshFocusableElements()
  }

  _handleSubmenuSummaryClick(event) {
    const summary = event.currentTarget
    const details = summary?.closest("details.mobile-menu__submenu")
    if (!details || this._motionMediaQuery.matches) return

    event.preventDefault()

    if (details.hasAttribute("open")) {
      this._animateSubmenuClose(details)
      return
    }

    this._animateSubmenuOpen(details)
  }

  _getDetailsContentElement(detailsEl) {
    if (!detailsEl) return null
    return detailsEl.querySelector(".mobile-menu__submenu__content")
  }

  _cancelDetailsAnimation(detailsEl) {
    const animation = this._detailsAnimations.get(detailsEl)
    if (!animation) return
    animation.cancel()
    this._detailsAnimations.delete(detailsEl)
  }

  _animateSubmenuOpen(detailsEl) {
    const content = this._getDetailsContentElement(detailsEl)

    detailsEl.setAttribute("open", "")
    this._syncDetailsAria(detailsEl)
    this._updateSubmenuItemsTabindex(detailsEl)
    this._refreshFocusableElements()

    if (!content) return

    this._cancelDetailsAnimation(detailsEl)

    content.style.height = ""
    content.style.overflow = ""
    content.style.opacity = ""
    content.style.paddingTop = ""
    content.style.paddingBottom = ""

    const computedStyles = window.getComputedStyle(content)
    const targetPaddingTop = computedStyles.paddingTop
    const targetPaddingBottom = computedStyles.paddingBottom
    const targetHeight = content.scrollHeight

    content.style.height = "0px"
    content.style.overflow = "hidden"
    content.style.paddingTop = "0px"
    content.style.paddingBottom = "0px"
    const animation = content.animate(
      [
        {
          height: "0px",
          opacity: 0,
          paddingTop: "0px",
          paddingBottom: "0px",
        },
        {
          height: `${targetHeight}px`,
          opacity: 1,
          paddingTop: targetPaddingTop,
          paddingBottom: targetPaddingBottom,
        },
      ],
      { duration: 200, easing: "ease" },
    )

    this._detailsAnimations.set(detailsEl, animation)

    animation.onfinish = () => {
      this._detailsAnimations.delete(detailsEl)
      content.style.height = ""
      content.style.overflow = ""
      content.style.opacity = ""
      content.style.paddingTop = ""
      content.style.paddingBottom = ""
    }

    animation.oncancel = () => {
      content.style.height = ""
      content.style.overflow = ""
      content.style.opacity = ""
      content.style.paddingTop = ""
      content.style.paddingBottom = ""
    }
  }

  _animateSubmenuClose(detailsEl) {
    const content = this._getDetailsContentElement(detailsEl)

    if (!content) {
      detailsEl.removeAttribute("open")
      this._syncDetailsAria(detailsEl)
      this._updateSubmenuItemsTabindex(detailsEl)
      this._refreshFocusableElements()
      return
    }

    this._cancelDetailsAnimation(detailsEl)

    content.style.height = ""
    content.style.overflow = ""
    content.style.opacity = ""
    content.style.paddingTop = ""
    content.style.paddingBottom = ""

    const computedStyles = window.getComputedStyle(content)
    const startPaddingTop = computedStyles.paddingTop
    const startPaddingBottom = computedStyles.paddingBottom
    const startHeight = content.scrollHeight
    content.style.height = `${startHeight}px`
    content.style.overflow = "hidden"

    const links = detailsEl.querySelectorAll(".mobile-menu__submenu__items a")
    for (const link of links) {
      link.setAttribute("tabindex", "-1")
    }
    this._refreshFocusableElements()

    const animation = content.animate(
      [
        {
          height: `${startHeight}px`,
          opacity: 1,
          paddingTop: startPaddingTop,
          paddingBottom: startPaddingBottom,
        },
        {
          height: "0px",
          opacity: 0,
          paddingTop: "0px",
          paddingBottom: "0px",
        },
      ],
      { duration: 180, easing: "ease" },
    )

    this._detailsAnimations.set(detailsEl, animation)

    animation.onfinish = () => {
      this._detailsAnimations.delete(detailsEl)
      detailsEl.removeAttribute("open")
      this._syncDetailsAria(detailsEl)
      content.style.height = ""
      content.style.overflow = ""
      content.style.opacity = ""
      content.style.paddingTop = ""
      content.style.paddingBottom = ""
      this._updateSubmenuItemsTabindex(detailsEl)
      this._refreshFocusableElements()
    }

    animation.oncancel = () => {
      content.style.height = ""
      content.style.overflow = ""
      content.style.opacity = ""
      content.style.paddingTop = ""
      content.style.paddingBottom = ""
    }
  }

  _syncDetailsAria(detailsEl) {
    if (!detailsEl) return
    const summary = detailsEl.querySelector("summary.mobile-menu__submenu__summary")
    if (!summary) return
    summary.setAttribute("aria-expanded", detailsEl.hasAttribute("open") ? "true" : "false")
  }

  _handleParentItemKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this._handleSubMenuOpen(event)
    }
  }

  _handleCloseSubpanelClick() {
    this._handleSubMenuClose(true)
  }

  _handleSubMenuOpen = (event) => {
    event.preventDefault()

    const toggle = event.currentTarget
    const targetId =
      toggle?.getAttribute("aria-controls") ||
      toggle?.getAttribute("data-target") ||
      ""

    if (!targetId) return

    let activePanel = null

    for (const item of this.subPanels) {
      if (item.id === targetId || item.getAttribute("data-panel-id") === targetId) {
        item.classList.remove("transparent")
        item.hidden = false
        item.setAttribute("aria-hidden", "false")
        activePanel = item
      } else {
        item.classList.add("transparent")
        item.hidden = true
        item.setAttribute("aria-hidden", "true")
      }
    }

    if (!activePanel) return

    for (const item of this.parentItems) {
      item.setAttribute("aria-expanded", "false")
    }
    toggle.setAttribute("aria-expanded", "true")

    this.$container.classList.add("mobile-submenu__opened")

    this._activeSubPanel = activePanel
    this._activeToggle = toggle

    if (this.closeSubpanelBtn) {
      this.closeSubpanelBtn.setAttribute("tabindex", "0")
    }

    this._refreshFocusableElements()
    const firstFocusable = this.allFocusableElements[0]

    if (firstFocusable) {
      firstFocusable.focus()
    } else if (this._activeSubPanel) {
      this._activeSubPanel.setAttribute("tabindex", "-1")
      this._activeSubPanel.focus()
    }
  }

  _handleSubMenuClose = (restoreFocus = false) => {
    this.$container.classList.remove("mobile-submenu__opened")

    for (const item of this.subPanels) {
      item.classList.add("transparent")
      item.hidden = true
      item.setAttribute("aria-hidden", "true")
    }

    if (this.closeSubpanelBtn) {
      this.closeSubpanelBtn.setAttribute("tabindex", "-1")
    }

    if (this._activeSubPanel) {
      this._activeSubPanel = null
    }

    this.allFocusableElements = []
    this._refreshFocusableElements()

    if (this._activeToggle) {
      this._activeToggle.setAttribute("aria-expanded", "false")
      if (restoreFocus) {
        this._activeToggle.focus()
      }
      this._activeToggle = null
    }
  }

  _isSubMenuOpen() {
    return this._activeSubPanel && this.$container.classList.contains("mobile-submenu__opened")
  }

  _handleSubMenuKeydown(event) {
    if (event.key === "Escape" || event.key === "Esc") {
      if (this._isSubMenuOpen()) {
        event.stopPropagation()
        event.preventDefault()
        this._handleSubMenuClose(true)
      }
      return
    }

    if (event.key !== "Tab") return

    if (!this._isSubMenuOpen()) {
      return
    }

    if (!this.allFocusableElements.length) {
      this._refreshFocusableElements()
    }
    const focusableElements = this.allFocusableElements
    if (!focusableElements.length) return

    const first = focusableElements[0]
    const last = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  _collectAllPotentialFocusableElements() {
    const selectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "details > summary",
      "[tabindex]",
    ]

    this._allPotentialFocusableElements = Array.from(
      this.querySelectorAll(selectors.join(",")),
    ).filter((el) => {
      if (el.hasAttribute("disabled")) return false
      if (el.closest("[hidden]")) return false
      const ariaHidden = el.getAttribute("aria-hidden")
      if (ariaHidden === "true") return false
      return true
    })
  }

  _getFocusableElements(root) {
    if (!root) return []

    const selectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "details > summary",
      "[tabindex]",
    ]

    let candidates = Array.from(
      root.querySelectorAll(selectors.join(",")),
    ).filter((el) => {
      if (el.hasAttribute("disabled")) return false
      if (el.closest("[hidden]")) return false
      const ariaHidden = el.getAttribute("aria-hidden")
      if (ariaHidden === "true") return false
      return true
    })

    if (root === this._activeSubPanel) {
      const extras = [this.closeSubpanelBtn, this.menuCloseBtn].filter(Boolean)
      for (const extra of extras) {
        if (
          !candidates.includes(extra) &&
          !extra.hasAttribute("disabled") &&
          !extra.closest("[hidden]") &&
          extra.getAttribute("aria-hidden") !== "true"
        ) {
          candidates.push(extra)
        }
      }
    }

    return candidates
  }

  _refreshFocusableElements() {
    const root = this._activeSubPanel || this.$container || this
    if (!root) {
      this.allFocusableElements = []
      return
    }

    this._collectAllPotentialFocusableElements()
    this.allFocusableElements = this._getFocusableElements(root)

    this._updateTabindexForAllElements()
  }

  _updateTabindexForAllElements() {
    for (const element of this._allPotentialFocusableElements) {
      element.setAttribute("tabindex", "-1")
    }

    for (const element of this.allFocusableElements) {
      element.removeAttribute("tabindex")
    }
  }

  _updateSubmenuItemsTabindex(detailsEl) {
    if (!detailsEl) return
    const isOpen = detailsEl.hasAttribute("open")
    const links = detailsEl.querySelectorAll(
      ".mobile-menu__submenu__items a",
    )
    const tabIndex = isOpen ? "0" : "-1"
    for (const link of links) {
      link.setAttribute("tabindex", tabIndex)
    }

    this._refreshFocusableElements()
  }

  render() {
    return html` <slot></slot> `
  }
}

customElements.define("mobile-menu-panel", MobileMenuPanel)
